import { Autocomplete, Divider, Paper, TextField, Drawer, Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { useDependencyInjection } from "../../config/di/dependency_injection_context";
import { usePageLoader } from "../contexts/page_content_context";
import { usePageStartBootstrap } from "../hooks/use_page_start_bootstrap";
import { FormCPFMaskedInput } from "./masked_inputs/react_forms/form_cpf_masked_input";
import { zodResolver } from "@hookform/resolvers/zod";
import { DropDown } from "../domain/models/dropdown";
import { FornecedorDropDown } from "../domain/models/fornecedor_dropdown";
import { DefaultFormAutocomplete } from "./default_form_autocomplete";
import { FormCNPJMaskedInput } from "./masked_inputs/react_forms/form_cnpj_masked_input";
import {
  defaultFornecedorSimplificadoForm,
  FornecedorSimplificadoForm,
  FornecedorSimplificadoSchema,
} from "../presentation/schemas/FornecedorSimplificado";
import { FormSwitchInput } from "./masked_inputs/react_forms/form_switch_input";

import DynamicIcon from "./dynamic_icon";
import DefaultFormInput from "./default_form_input";
import PrimaryButton from "./primary_button";
import { processError } from "../utils/base_error";

/**
 * DropDownFornecedor
 */

type Props<F extends {}> = {
  name: keyof F;
  label?: string;
  newButtonLabel?: string;
  required?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  reactHookForm: UseFormReturn<F>;
  tercerizacao?: boolean;
  FornServico?: boolean;
};

export const DropDownFornecedor = <F extends {}>({
  name,
  label,
  newButtonLabel,
  required,
  readOnly,
  placeholder,
  reactHookForm,
  tercerizacao,
  FornServico
}: Props<F>) => {
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [fornecedorDropDown, setFornecedorDropDown] = useState<FornecedorDropDown[]>([]);
  const [centroCustoDropDown, setCentroCustoDropDown] = useState<DropDown[]>([]);

  const {
    commons: { getFornecedorDropDownUseCase, getCentroCustoDropDownUseCase },
    compras: { createFornecedorSimplificadoUseCase },
  } = useDependencyInjection();

  const hookForm = useForm<FornecedorSimplificadoForm>({
    reValidateMode: "onSubmit",
    mode: "onSubmit",
    defaultValues: defaultFornecedorSimplificadoForm,
    resolver: zodResolver(FornecedorSimplificadoSchema),
  });

  useEffect(() => {
    if (openModal && tercerizacao) {
      hookForm.setValue("fornProduto", false);
      hookForm.setValue("fornServico", true);
    }
  }, [openModal, tercerizacao, hookForm]);

  const loadFornecedorDropDown = useCallback(async () => {
    try {
      const result = await getFornecedorDropDownUseCase.getFornecedorDropDown(undefined, FornServico);
      setFornecedorDropDown(result);
    } catch (e: unknown) {
      pushErrorNotification("Error", processError(e, "Falha ao buscar Fornecedores!"));
    }
  }, [FornServico, getFornecedorDropDownUseCase, pushErrorNotification]);

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      setCentroCustoDropDown(result);
    } catch (e: unknown) {
      pushErrorNotification("Error", processError(e, "Falha ao buscar centro Custo!"));
    }
  }, [getCentroCustoDropDownUseCase, pushErrorNotification]);

  useEffect(() => {
    loadFornecedorDropDown();
    loadCentroCustoDropDown();
  }, [loadFornecedorDropDown, loadCentroCustoDropDown]);

  const hanldleSalvarFornecedor = useCallback(
    async (form: FornecedorSimplificadoForm) => {
      try {
        setSaving(true);
        const idFornecedor = await createFornecedorSimplificadoUseCase.createFornecedorSimplificado(form);

        reactHookForm.setValue(name as any, idFornecedor as any);
        pushInfoNotification("SUCESSO", "Fornecedor criado com sucesso!!", { isAutoFinish: true });
      } catch (e: unknown) {
        pushErrorNotification("Erro", ["Falha ao salvar Fornecedor"], { isAutoFinish: true });
      } finally {
        setSaving(false);
        loadFornecedorDropDown();
        setOpenModal(false);
        hookForm.reset();
      }
    },
    [
      hookForm,
      createFornecedorSimplificadoUseCase,
      pushInfoNotification,
      loadFornecedorDropDown,
      reactHookForm,
      name,
      pushErrorNotification,
    ],
  );

  // BOTÃO
  const CustomPaper = useMemo(
    () =>
      ({ children, ...props }: any) => (
        <Paper {...props}>
          <div>
            {children}
            <Divider />
            <div>
              <div
                className="py-2 px-4 cursor-pointer rounded-[4px] font-semibold flex items-center gap-2 text-sm text-primary-green hover:bg-hover-light-gray"
                onMouseDown={() => setOpenModal(true)}
                role="button"
                tabIndex={0}
              >
                <DynamicIcon nameIcon="Add" size={20} className="text-primary-green" />
                <span>{newButtonLabel ?? "Novo"}</span>
              </div>
            </div>
          </div>
        </Paper>
      ),
    [newButtonLabel],
  );

  return (
    <>
      {/*DROPDOWN */}
      <Controller
        name={name as any}
        control={reactHookForm.control}
        render={({ field, formState }) => {
          let helperText: string | null = null;

          if (formState.errors[name as keyof typeof formState.errors]) {
            const fieldError = formState.errors[name] as any;
            if (fieldError?.message && typeof fieldError.message === "string") {
              helperText = fieldError.message;
            }
          }

          return (
            <Autocomplete
              fullWidth={true}
              size="small"
              options={fornecedorDropDown}
              getOptionKey={(opt) => opt.idFornecedor}
              getOptionLabel={(opt) => opt.nomeFantasia}
              isOptionEqualToValue={(opt, val) => opt.idFornecedor === val.idFornecedor}
              slots={{
                paper: CustomPaper,
              }}
              sx={{ minHeight: 45 }}
              readOnly={readOnly}
              disabled={formState.disabled}
              value={fornecedorDropDown.find((x) => x.idFornecedor === field.value) ?? null}
              onChange={(_, newValue) => field.onChange(newValue ? newValue.idFornecedor : undefined)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={label}
                  error={helperText !== null}
                  helperText={helperText}
                  placeholder={placeholder}
                  required={required}
                />
              )}
            />
          );
        }}
      />

      {/* CADASTRO MODAL */}
      <Drawer
        anchor="right"
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          hookForm.reset();
        }}
        style={{ zIndex: 1300 }}
        PaperProps={{ style: { width: 500, padding: 20, display: "flex", flexDirection: "column", height: "100%" } }}
      >
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <h2 className="text-lg font-semibold mb-4">Adicionar Fornecedor</h2>

          <div className="flex flex-col items-start w-[25rem] gap-4">
            <div className="w-full p-4 rounded-2xl shadow-sm">
              <div className="flex items-center justify-between">
                <label className="font-medium text-gray-700">Ativo:</label>
                <FormSwitchInput activeLabel="Sim" inactiveLabel="Não" name="ativo" readOnly={saving} reactHookForm={hookForm} />
              </div>

              <div className="flex items-center justify-between">
                <label className="font-medium text-gray-700">Avaliar Fornecedor?</label>
                <FormSwitchInput activeLabel="Sim" inactiveLabel="Não" name="avaliar" readOnly={saving} reactHookForm={hookForm} />
              </div>

              <div className="flex items-center justify-between">
                <label className="font-medium text-gray-700">Fornecedor de Produtos?</label>
                <FormSwitchInput activeLabel="Sim" inactiveLabel="Não" name="fornProduto" readOnly={saving} reactHookForm={hookForm} />
              </div>

              <div className="flex items-center justify-between">
                <label className="font-medium text-gray-700">Fornecedor de serviços?</label>
                <FormSwitchInput activeLabel="Sim" inactiveLabel="Não" name="fornServico" readOnly={saving} reactHookForm={hookForm} />
              </div>
            </div>

            <DefaultFormInput name="nomeFantasia" label="Nome Fantasia" required={true} reactHookForm={hookForm} />

            <div className="w-full">
              <DefaultFormAutocomplete
                name="idCentroCusto"
                required={false}
                label="Centro Custo"
                options={centroCustoDropDown ?? []}
                getOptionLabel={(x) => x.title}
                getOptionKey={(x) => x.id}
                reactHookForm={hookForm}
              />
            </div>

            <FormCNPJMaskedInput name="cnpj" label="CNPJ" required={false} reactHookForm={hookForm} />

            <FormCPFMaskedInput name="cpf" label="CPF" required={false} reactHookForm={hookForm} />

            <DefaultFormInput
              name="observacao"
              label="Observações"
              multiline={true}
              minRows={4}
              required={false}
              reactHookForm={hookForm}
            />

            <div className="w-full p-4 rounded-2xl shadow-sm">
              <div className="flex items-center justify-between">
                <label className="font-medium text-gray-700">Qualificação:</label>
                <FormSwitchInput activeLabel="Outros" inactiveLabel="Não" name="outro" readOnly={true} reactHookForm={hookForm} />
              </div>
            </div>

            <div className="w-full p-4 rounded-2xl shadow-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium text-red-700">
                  Recomenda-se que a qualificação do fornecedor seja finalizada em seu cadastro!
                </span>
              </div>
            </div>
          </div>
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            background: "white",
            padding: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
            onClick={() => {
              setOpenModal(false);
              hookForm.reset();
            }}
          >
            <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
          </Box>

          <Box width="120px">
            {saving ? (
              <PrimaryButton buttonColor="primary-green">Salvando...</PrimaryButton>
            ) : (
              <PrimaryButton buttonColor="primary-green" onClick={hookForm.handleSubmit(hanldleSalvarFornecedor)}>
                Salvar
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
